/**
 * @file    OptionsSection.tsx
 * @scope   VIEW
 * @summary "Options" accordion of the drawer main view: the chart's actions and display options.
 * @consumes DrawerContext (customWidget, currentOptionsAlias), widgetOptionsBuilder, useWidgetOptions
 * @produces JSX only
 * @sideEffects None directly — every change is delegated to the injected handlers, which publish
 *              the edited widget to the global store.
 * @notes LEGACY-ONLY: Enterprise V2 widgets expose a fixed action set, so the caller does not
 *        render this section for them.
 */
import React, { useState } from 'react';
import Accordion from 'core-ui/accordionDrawer/accordionDrawer';
import OptionCheckbox from 'core-ui/optionCheckbox/optionCheckbox';
import CustomOptionCheckbox from 'core-ui/customOptionCheckbox/customOptionCheckbox';
import SelectDecimalPlaces from 'core-ui/SelectDecimalPlaces/SelectDecimalPlaces';
import { diyActionsOptions, diyChartOptions } from 'constants/diyDrawerOptions';
import { ChartOptionSelector } from 'constants/chartOptionsSelector';
import {
  buildChartActions,
  buildChartOptions,
  OptionDescriptor,
  resolveDecimalPlacesDescriptor,
} from '../../../common/utils/widgetOptionsBuilder';
import { WidgetOptions } from '../../../common/hooks/useWidgetOptions';
import { useDrawerContext } from '../../../State/DrawerContext';

export interface OptionsSectionProps {
  /** Change handlers produced by `useWidgetOptions`. */
  handlers: WidgetOptions;
}

const OptionsSection: React.FC<OptionsSectionProps> = ({ handlers }) => {
  // WHY owned here: the expanded flag is pure UI state nobody else reads, so keeping it in the
  // drawer only forced the whole drawer to re-render on every toggle.
  const [isExpanded, setIsExpanded] = useState(false);
  const { customWidget, currentOptionsAlias } = useDrawerContext();
  const { handleActions, handleOptions, handleDecimalChange, getSetter, getSetterValues } =
    handlers;

  /** True when a descriptor must be rendered as the decimal-places dropdown. */
  const isDecimalPlacesDescriptor = (item: OptionDescriptor) =>
    typeof item.isChecked === 'undefined' &&
    typeof item.value !== 'undefined' &&
    item.childComponent === undefined;

  const renderDecimalPlaces = (item: OptionDescriptor) => (
    <SelectDecimalPlaces
      {...{
        option: resolveDecimalPlacesDescriptor(customWidget, item),
        onChangeHandler: handleDecimalChange,
      }}
      key={item.actionName}
    />
  );

  const renderAction = (item: OptionDescriptor) =>
    isDecimalPlacesDescriptor(item) ? (
      renderDecimalPlaces(item)
    ) : (
      <OptionCheckbox
        {...{ option: item, onChangeHandler: handleActions }}
        size="default"
        key={item.actionName}
      />
    );

  const renderOption = (item: OptionDescriptor, options: any) => {
    if (isDecimalPlacesDescriptor(item)) return renderDecimalPlaces(item);
    if (item.childComponent !== undefined) {
      return (
        <CustomOptionCheckbox
          {...{
            option: item,
            onChangeHandler: handleOptions,
            ChildComponentName: item.childComponent as ChartOptionSelector,
            onSetGoalValuesChange: getSetter(item.childComponent),
            customValues: getSetterValues(item.childComponent, options),
            showChildComponent: item.isChecked,
          }}
          key={item.actionName}
        />
      );
    }
    return (
      <OptionCheckbox
        {...{ option: item, onChangeHandler: handleOptions }}
        size="default"
        key={item.actionName}
      />
    );
  };

  const { alias, options } = customWidget;
  const actionDescriptors = buildChartActions(diyActionsOptions, alias, options?.actions);
  const optionDescriptors = buildChartOptions(
    diyChartOptions,
    currentOptionsAlias(alias),
    customWidget
  );

  return (
    <div className="diy-drawer-section">
      <Accordion title="Options" isExpanded={isExpanded} onToggle={setIsExpanded}>
        {options && <>{actionDescriptors?.map(renderAction)}</>}
        {options && <>{optionDescriptors?.map((item) => renderOption(item, options))}</>}
      </Accordion>
    </div>
  );
};

export default React.memo(OptionsSection);
